import React, { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  MapPin,
  Briefcase,
  Award,
  ThumbsUp,
  ThumbsDown,
  Download,
  Archive,
  Trash2,
  Loader2,
  AlertCircle,
  UserPlus,
  Check,
} from 'lucide-react'
import type { AIMatchResult } from '@/types'

interface MatchResultCardProps {
  result: AIMatchResult
  onFeedback: (
    feedback: 'RELEVANT' | 'NOT_RELEVANT' | 'SHORTLISTED',
    notes?: string
  ) => Promise<void>
  rank: number
  onArchive?: () => Promise<void>
  onDelete?: () => Promise<void>
  onAddToProspects?: (candidateId: string, aiMatchResultId: string) => Promise<void>
}

export const MatchResultCard: React.FC<MatchResultCardProps> = ({
  result,
  onFeedback,
  onArchive,
  onDelete,
  onAddToProspects,
}) => {
  const [showDetails, setShowDetails] = useState(false)
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false)
  const [feedbackNotes, setFeedbackNotes] = useState(result.feedbackNotes || '')
  const [isArchiving, setIsArchiving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isAddingToProspects, setIsAddingToProspects] = useState(false)
  const [addedToProspects, setAddedToProspects] = useState(false)

  const isProspect = result.prospect != null || addedToProspects
  const prospectStatus = result.prospect?.status ?? (addedToProspects ? 'ADDED' : null)

  const candidate = result.candidate

  const handleFeedback = async (feedback: 'RELEVANT' | 'NOT_RELEVANT' | 'SHORTLISTED') => {
    setIsSubmittingFeedback(true)
    try {
      await onFeedback(feedback, feedbackNotes)
    } finally {
      setIsSubmittingFeedback(false)
    }
  }

  const handleArchive = async () => {
    if (!onArchive) return
    setIsArchiving(true)
    try {
      await onArchive()
    } finally {
      setIsArchiving(false)
    }
  }

  const handleDelete = async () => {
    if (!onDelete) return
    setIsDeleting(true)
    try {
      await onDelete()
    } finally {
      setIsDeleting(false)
    }
  }

  const handleAddToProspects = async () => {
    if (!onAddToProspects) return
    setIsAddingToProspects(true)
    try {
      await onAddToProspects(result.candidateId, result.id)
      setAddedToProspects(true)
    } finally {
      setIsAddingToProspects(false)
    }
  }

  // Deterministic bluey avatar based on candidate ID
  const blueyAvatarUrl = useMemo(() => {
    const id = result.candidateId
    const num = ((id.charCodeAt(0) + id.charCodeAt(id.length - 1)) % 10) + 1
    return `https://cdn.jsdelivr.net/gh/alohe/avatars/png/bluey_${num}.png`
  }, [result.candidateId])

  // Helper to get badge style based on label
  const getBadgeStyle = (label: string) => {
    if (label === 'Best Overall Match') {
      return 'bg-blue-600 text-white border-blue-700'
    } else if (label === 'Top Match') {
      return 'bg-blue-500 text-white border-blue-600'
    } else if (label.startsWith('Strong ') && label.endsWith(' Fit')) {
      return 'bg-purple-100 text-purple-800 border-purple-300'
    } else if (label === 'Good Skill Match') {
      return 'bg-green-100 text-green-800 border-green-300'
    } else if (label === 'Potential Match') {
      return 'bg-gray-100 text-gray-700 border-gray-300'
    } else {
      return 'bg-blue-100 text-blue-800 border-blue-300'
    }
  }

  // If candidate data is not populated, show a minimal card
  if (!candidate) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">
              #{result.rankPosition}
            </div>
            <img
              src={blueyAvatarUrl}
              alt="Candidate avatar"
              className="w-12 h-12 rounded-full rounded-avatar object-cover"
            />
            <div>
              <h3 className="font-semibold text-lg text-gray-900">
                Candidate #{result.candidateId.slice(0, 8)}
              </h3>
              <p className="text-sm text-gray-600">Candidate data not loaded</p>
            </div>
          </div>
        </div>
        <p className="text-sm text-gray-700 mb-3">{result.matchExplanation}</p>
        <p className="text-sm text-orange-600">
          Note: Full candidate details are not available. Please check the backend API response.
        </p>
      </div>
    )
  }

  // Best match styling - apply special styling for rank #1
  const isBestMatch = result.rankPosition === 1

  return (
    <div
      className={`bg-white rounded-lg transition-shadow relative overflow-hidden ${
        result.archived ? 'opacity-60 bg-gray-50' : ''
      } ${
        isBestMatch
          ? 'border-2 border-amber-400 shadow-lg shadow-amber-100/50'
          : 'border border-gray-200 shadow-sm hover:shadow-md'
      }`}
    >
      {/* Status Badges - shown in top right */}
      {result.archived && (
        <div className="absolute top-2 right-2 z-10">
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-300">
            <Archive className="w-3 h-3" />
            Archived
          </span>
        </div>
      )}

      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">
              #{result.rankPosition}
            </div>
            {candidate?.profilePhotoUrl || candidate?.profilePictureUrl ? (
              <img
                src={candidate.profilePhotoUrl || candidate.profilePictureUrl}
                alt={`${candidate.firstName} ${candidate.lastName}`}
                className="w-12 h-12 rounded-full rounded-avatar object-cover flex-shrink-0"
              />
            ) : (
              <img
                src={blueyAvatarUrl}
                alt={`${candidate?.firstName} ${candidate?.lastName}`}
                className="w-12 h-12 rounded-full rounded-avatar object-cover flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg text-gray-900">
                {candidate?.firstName || 'Unknown'} {candidate?.lastName || 'Candidate'}
              </h3>
              <p className="text-sm text-gray-600">
                {candidate?.jobTitle || candidate?.title || 'No title'}
              </p>

              {/* Match Labels */}
              {result.matchLabels && result.matchLabels.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {result.matchLabels.map((label, idx) => (
                    <span
                      key={idx}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getBadgeStyle(label)}`}
                    >
                      {label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Add to Prospects — top right of header */}
          {onAddToProspects && (
            <button
              onClick={handleAddToProspects}
              disabled={isAddingToProspects || isProspect}
              className={`flex-shrink-0 flex items-center gap-1.5 py-1.5 px-3 rounded-lg text-xs font-semibold transition-colors disabled:cursor-not-allowed ${
                isProspect
                  ? prospectStatus === 'OFFER_ACCEPTED'
                    ? 'bg-green-100 text-green-700 border border-green-200'
                    : prospectStatus === 'OFFER_REJECTED'
                      ? 'bg-red-100 text-red-700 border border-red-200'
                      : prospectStatus === 'CONTACTED'
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'bg-green-100 text-green-700 border border-green-200'
                  : 'bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50'
              }`}
              aria-label="Add to prospects"
            >
              {isAddingToProspects ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Adding...
                </>
              ) : isProspect ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  {prospectStatus === 'CONTACTED'
                    ? 'Contacted'
                    : prospectStatus === 'OFFER_ACCEPTED'
                      ? 'Offer Accepted'
                      : prospectStatus === 'OFFER_REJECTED'
                        ? 'Offer Rejected'
                        : 'Added'}
                </>
              ) : (
                <>
                  <UserPlus className="w-3.5 h-3.5" />
                  Add to Prospects
                </>
              )}
            </button>
          )}
        </div>

        {/* Quick Info */}
        <div className="mt-3 flex flex-wrap gap-3 text-sm text-gray-600">
          {candidate?.location && (
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              <span>{candidate.location}</span>
            </div>
          )}
          {(candidate?.yearsExperience ?? candidate?.yearsOfExperience) !== undefined && (
            <div className="flex items-center gap-1">
              <Briefcase className="w-4 h-4" />
              <span>
                {candidate.yearsExperience ?? candidate.yearsOfExperience} years experience
              </span>
            </div>
          )}
          {candidate?.profileCompletion !== undefined && (
            <div className="flex items-center gap-1">
              <Award className="w-4 h-4" />
              <span>{candidate.profileCompletion}% complete</span>
            </div>
          )}
        </div>
      </div>

      {/* Match Explanation */}
      <div className="p-4">
        <p className="text-sm text-gray-700 mb-3">{result.matchExplanation}</p>

        {/* Missing Skills Warning */}
        {result.missingSkills && result.missingSkills.length > 0 && (
          <div className="flex items-start gap-2 p-3 mb-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900 mb-1">Missing Skills:</p>
              <p className="text-sm text-amber-800">{result.missingSkills.join(', ')}</p>
            </div>
          </div>
        )}

        {/* Strengths & Weaknesses Toggle - Only show if LLM analysis is available */}
        {(result.strengths || result.weaknesses) && (
          <>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              {showDetails ? 'Hide Details' : 'Show Details'}
            </button>

            {showDetails && (
              <div className="mt-3 space-y-3">
                {/* Strengths */}
                {result.strengths && result.strengths.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-green-700 mb-1">Strengths</h4>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
                      {result.strengths.map((strength, idx) => (
                        <li key={idx}>{strength}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Weaknesses */}
                {result.weaknesses && result.weaknesses.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-orange-700 mb-1">Areas for Growth</h4>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
                      {result.weaknesses.map((weakness, idx) => (
                        <li key={idx}>{weakness}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 bg-gray-50 border-t border-gray-100">
        {/* Notes */}
        <textarea
          value={feedbackNotes}
          onChange={(e) => setFeedbackNotes(e.target.value)}
          placeholder="Add notes about this candidate..."
          className="w-full text-sm p-2 border border-gray-300 rounded-md resize-none mb-3"
          rows={2}
        />

        {/* Bottom row: profile/cv/archive/delete on left, feedback icons on right */}
        <div className="flex items-center gap-2">
          {/* Left: secondary actions */}
          {candidate?.username ? (
            <Link
              to={`/profile/${candidate.username}`}
              className="flex-1 text-center py-2 px-4 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm font-medium"
            >
              View Full Profile
            </Link>
          ) : (
            <div className="flex-1 text-center py-2 px-4 bg-gray-100 text-gray-400 rounded-md text-sm font-medium cursor-not-allowed">
              Profile Not Available
            </div>
          )}
          {candidate?.cvFileUrl && (
            <a
              href={candidate.cvFileUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center py-2 px-3 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              aria-label="Download CV"
            >
              <Download className="w-4 h-4" />
            </a>
          )}
          {onArchive && (
            <button
              onClick={handleArchive}
              disabled={isArchiving}
              className="flex items-center justify-center py-2 px-3 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Archive match"
            >
              {isArchiving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Archive className="w-4 h-4" />
              )}
            </button>
          )}
          {onDelete && (
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex items-center justify-center py-2 px-3 bg-red-50 text-red-600 rounded-md hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed border border-red-200"
              aria-label="Delete match"
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </button>
          )}

          {/* Right: compact icon-only feedback buttons */}
          <div className="flex items-center gap-1 ml-auto pl-2 border-l border-gray-200">
            <button
              onClick={() => handleFeedback('RELEVANT')}
              disabled={isSubmittingFeedback}
              title="Relevant"
              className={`flex items-center justify-center p-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                result.recruiterFeedback === 'RELEVANT'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-500 hover:text-green-600 hover:border-green-400'
              }`}
            >
              {isSubmittingFeedback ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ThumbsUp className="w-4 h-4" />
              )}
            </button>
            <button
              onClick={() => handleFeedback('NOT_RELEVANT')}
              disabled={isSubmittingFeedback}
              title="Not Relevant"
              className={`flex items-center justify-center p-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                result.recruiterFeedback === 'NOT_RELEVANT'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-500 hover:text-red-500 hover:border-red-400'
              }`}
            >
              <ThumbsDown className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
